'use client';

import React from 'react';
import { Box, Container, Skeleton } from '@mui/material';

const DASHBOARD_CARD_SX = {
  borderRadius: 3,
  border: '1px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
} as const;

const CARD_HEADER = (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.25,
      px: { xs: 2.5, md: 3 },
      pt: { xs: 2.5, md: 3 },
      pb: 1.5,
    }}
  >
    <Skeleton variant="rounded" width={34} height={34} sx={{ borderRadius: 2 }} />
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Skeleton variant="text" width={160} height={22} />
      <Skeleton variant="text" width={200} height={16} />
    </Box>
  </Box>
);

/**
 * StreaksSkeleton
 * Loading placeholder for the streaks page, shaped like the real layout
 * (StreakDashboard's 8 sections) so there's no content jump once data
 * arrives.
 */
export const StreaksSkeleton: React.FC = () => {
  return (
    <Container maxWidth="lg" sx={{ pb: 10 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {/* StreakHero */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 4 }}>
          <Skeleton variant="text" width={140} height={20} />
          <Skeleton variant="text" width={320} height={56} />
          <Skeleton variant="text" width={420} height={24} />
        </Box>

        {/* StreakStatsRow */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
            gap: 2,
          }}
        >
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} variant="rounded" height={128} sx={{ borderRadius: 4 }} />
          ))}
        </Box>

        {/* StreakCycleTracker */}
        <Box sx={{ ...DASHBOARD_CARD_SX, borderRadius: 4, p: { xs: 2.5, md: 3 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Skeleton variant="text" width={160} height={24} />
            <Skeleton variant="text" width={70} height={20} />
          </Box>
          <Skeleton variant="text" width="70%" height={20} sx={{ mb: 2.5 }} />
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1.25 }}>
            {Array.from({ length: 7 }).map((_, i) => (
              <Skeleton key={i} variant="rounded" height={72} sx={{ borderRadius: 3 }} />
            ))}
          </Box>
          <Skeleton variant="rounded" height={8} sx={{ mt: 2.5, borderRadius: 4 }} />
        </Box>

        {/* StreakHowItWorks */}
        <Box>
          <Skeleton variant="text" width={220} height={32} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width={200} height={20} sx={{ mb: 3 }} />
          {Array.from({ length: 5 }).map((_, i) => (
            <Box
              key={i}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 2,
                py: 2.25,
                borderBottom: i < 4 ? '1px solid' : 'none',
                borderColor: 'divider',
              }}
            >
              <Skeleton
                variant="rounded"
                width={40}
                height={40}
                sx={{ borderRadius: 2.5, flexShrink: 0 }}
              />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="40%" height={22} />
                <Skeleton variant="text" width="85%" height={18} />
              </Box>
            </Box>
          ))}
        </Box>

        {/* StreakBadgesShowcase */}
        <Box>
          <Skeleton variant="text" width={100} height={32} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width={260} height={20} sx={{ mb: 2.5 }} />
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(3, 1fr)',
                sm: 'repeat(4, 1fr)',
                md: 'repeat(7, 1fr)',
              },
              gap: 1.5,
            }}
          >
            {Array.from({ length: 7 }).map((_, i) => (
              <Skeleton key={i} variant="rounded" height={90} sx={{ borderRadius: 3 }} />
            ))}
          </Box>
        </Box>

        {/* StreakFreezeCard */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            gap: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 4,
            p: { xs: 2.5, md: 3 },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <Skeleton
              variant="rounded"
              width={44}
              height={44}
              sx={{ borderRadius: 3, flexShrink: 0 }}
            />
            <Box>
              <Skeleton variant="text" width={200} height={24} />
              <Skeleton variant="text" width={300} height={18} />
            </Box>
          </Box>
          <Skeleton variant="rounded" width={180} height={36} sx={{ borderRadius: 1 }} />
        </Box>

        {/* StreakLeaderboardSection */}
        <Box sx={DASHBOARD_CARD_SX}>
          {CARD_HEADER}
          <Box sx={{ px: { xs: 2.5, md: 3 }, pb: { xs: 2.5, md: 3 } }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {Array.from({ length: 5 }).map((_, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
                  <Skeleton variant="circular" width={36} height={36} />
                  <Skeleton variant="text" width="35%" />
                  <Skeleton variant="text" width={40} sx={{ ml: 'auto' }} />
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        {/* StreakHistorySection */}
        <Box sx={DASHBOARD_CARD_SX}>
          {CARD_HEADER}
          <Box sx={{ px: { xs: 2.5, md: 3 }, pb: { xs: 2.5, md: 3 } }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} variant="rounded" height={44} />
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default StreaksSkeleton;
